import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

type AttributeGroup = {
  id: string;
  name: string;
  slug: string;
};

type Attribute = {
  id: string;
  name: string;
  group_id: string;
};

const slugify = (value: string) =>
  value.toLowerCase().trim().replace(/\s+/g, "-");

const card = "bg-white border rounded-xl p-4 shadow-sm";
const input = "border p-3 w-full rounded-lg";
const button =
  "bg-[#5c2f2f] text-white px-4 py-2 rounded-lg hover:bg-[#7a4444]";

const AdminAttributePage = () => {
  const [groups, setGroups] = useState<AttributeGroup[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);

  const [newGroupName, setNewGroupName] = useState("");
  const [newAttrName, setNewAttrName] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");

  const [isAdmin, setIsAdmin] = useState(false);

  // ---------------- ADMIN CHECK ----------------
  useEffect(() => {
    const checkAdmin = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return;

      const { data } = await supabase
        .from("Admin")
        .select("*")
        .eq("user_id", user.id);

      if (data && data.length > 0) {
        setIsAdmin(true);
        fetchData();
      }
    };

    checkAdmin();
  }, []);

  // ---------------- FETCH ----------------
  const fetchData = async () => {
    const [g, a] = await Promise.all([
      supabase.from("attribute_groups").select("*").order("name"),
      supabase.from("attributes").select("*"),
    ]);

    if (g.data) setGroups(g.data);
    if (a.data) setAttributes(a.data);
  };

  // ---------------- ADD GROUP ----------------
  const addGroup = async () => {
    if (!newGroupName.trim()) return;

    const { data, error } = await supabase
      .from("attribute_groups")
      .insert({
        name: newGroupName,
        slug: slugify(newGroupName),
      })
      .select()
      .single();

    if (!error && data) {
      setGroups((prev) => [...prev, data]);
      setNewGroupName("");
    }
  };

  // ---------------- DELETE GROUP ----------------
  const deleteGroup = async (id: string) => {
    if (!window.confirm("Delete this group?")) return;

    await supabase.from("attributes").delete().eq("group_id", id);
    await supabase.from("attribute_groups").delete().eq("id", id);

    setGroups((prev) => prev.filter((g) => g.id !== id));
    setAttributes((prev) => prev.filter((a) => a.group_id !== id));
  };

  // ---------------- ADD ATTRIBUTE ----------------
  const addAttribute = async () => {
    if (!newAttrName.trim() || !selectedGroupId) return;

    const { data, error } = await supabase
      .from("attributes")
      .insert({
        name: newAttrName,
        slug: slugify(newAttrName),
        group_id: selectedGroupId,
      })
      .select()
      .single();

    if (!error && data) {
      setAttributes((prev) => [...prev, data]);
      setNewAttrName("");
    }
  };

  // ---------------- DELETE ATTRIBUTE ----------------
  const deleteAttribute = async (id: string) => {
    await supabase.from("attributes").delete().eq("id", id);
    setAttributes((prev) => prev.filter((a) => a.id !== id));
  };

  if (!isAdmin) {
    return <div className="p-6 text-center">Admin only</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6 bg-[#f8f6f4] min-h-screen">
      <h1 className="text-3xl font-bold text-[#5c2f2f]">
        Attribute Management
      </h1>

      {/* ADD GROUP */}
      <div className={card}>
        <h2 className="font-semibold mb-3">Add Attribute Group</h2>

        <input
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          placeholder="Example: Hair Type"
          className={input}
        />

        <button onClick={addGroup} className={`${button} mt-3`}>
          + Add Group
        </button>
      </div>

      {/* ADD ATTRIBUTE */}
      <div className={card}>
        <h2 className="font-semibold mb-3">Add Attribute</h2>

        <select
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          className={`${input} mb-2`}
        >
          <option value="">Select Group</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>

        <input
          value={newAttrName}
          onChange={(e) => setNewAttrName(e.target.value)}
          placeholder="Example: Curly"
          className={input}
        />

        <button onClick={addAttribute} className={`${button} mt-3`}>
          + Add Attribute
        </button>
      </div>

      {/* LIST */}
      <div className="space-y-4">
        {groups.length === 0 && (
          <p className="text-gray-500 text-sm">No groups yet</p>
        )}

        {groups.map((group) => {
          const groupAttributes = attributes.filter(
            (a) => a.group_id === group.id
          );

          return (
            <div key={group.id} className={card}>
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-semibold text-[#5c2f2f]">
                  {group.name}
                </h2>

                <button
                  onClick={() => deleteGroup(group.id)}
                  className="text-red-600 text-sm hover:underline"
                >
                  Delete Group
                </button>
              </div>

              {groupAttributes.length === 0 ? (
                <p className="text-sm text-gray-500">No attributes yet</p>
              ) : (
                <div className="space-y-2">
                  {groupAttributes.map((attr) => (
                    <div
                      key={attr.id}
                      className="flex justify-between items-center text-sm bg-gray-50 px-3 py-2 rounded"
                    >
                      {attr.name}
                      <button
                        onClick={() => deleteAttribute(attr.id)}
                        className="text-red-500 hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminAttributePage;